import DOMPurify from "dompurify";

// The server cleaner keeps the same three lists; both tests pin them to the same values.
export const ALLOWED_STYLE_PROPS = [
  "color",
  "background-color",
  "background",
  "text-align",
  "font-weight",
  "text-decoration",
];

export const ALLOWED_TAGS = [
  "b",
  "i",
  "u",
  "s",
  "strike",
  "strong",
  "em",
  "mark",
  "small",
  "del",
  "ins",
  "sub",
  "sup",
  "br",
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ol",
  "ul",
  "li",
  "blockquote",
  "a",
  "code",
  "pre",
  "span",
  "div",
  "font",
];

export const ALLOWED_ATTR = [
  "href",
  "target",
  "style",
  "color",
  "size",
  "face",
  "align",
];

// The business card keeps emphasis and lists only: its templates set their own sizes, colours and alignment.
export const BASIC_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "strike",
  "br",
  "p",
  "div",
  "ul",
  "ol",
  "li",
];

export type RichTextProfile = "full" | "basic";

const PROFILE_CONFIG: Record<
  RichTextProfile,
  { ALLOWED_TAGS: string[]; ALLOWED_ATTR: string[] }
> = {
  full: { ALLOWED_TAGS, ALLOWED_ATTR },
  basic: { ALLOWED_TAGS: BASIC_TAGS, ALLOWED_ATTR: [] },
};

// A private instance: hooks added to the shared DOMPurify would apply to every caller in the app.
const purifier = DOMPurify(window);

purifier.addHook("uponSanitizeAttribute", (_node, data) => {
  if (data.attrName === "style") {
    data.attrValue = data.attrValue
      .split(";")
      .map((rule) => rule.trim())
      .filter(
        (rule) =>
          ALLOWED_STYLE_PROPS.some((prop) =>
            rule.toLowerCase().startsWith(prop + ":")
          ) && !/url\s*\(/i.test(rule)
      )
      .join("; ");
  }
});

const protectLinkTarget = (node: Element) => {
  if (node.tagName !== "A" || !node.hasAttribute("target")) return;
  if (node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  } else {
    node.removeAttribute("target");
  }
};

purifier.addHook("afterSanitizeAttributes", protectLinkTarget);

export function sanitizeHtml(
  dirty: string | null | undefined,
  profile: RichTextProfile = "full"
): string {
  if (!dirty) return "";
  return purifier.sanitize(dirty, PROFILE_CONFIG[profile]);
}

// An e-mail keeps its own layout (tables, images, inline styles, <style>): only what can run, submit
// or load a page or a sheet of its own is removed. It is shown in a sandboxed frame, never in the page.
const emailPurifier = DOMPurify(window);

const EMAIL_CONFIG = {
  WHOLE_DOCUMENT: true,
  ADD_TAGS: ["style"],
  FORBID_TAGS: [
    "script",
    "iframe",
    "frame",
    "object",
    "embed",
    "form",
    "input",
    "button",
    "textarea",
    "select",
    "base",
    "meta",
    "link",
  ],
};

export function sanitizeEmailHtml(dirty: string | null | undefined): string {
  if (!dirty) return "";
  return emailPurifier.sanitize(dirty, EMAIL_CONFIG);
}

export default sanitizeHtml;
